import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Check, Loader2, User, Phone, Mail, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const ContactFormRino = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    mensaje: '',
    fotoFrente: null,
    fotoPerfil: null
  });
  
  const [previewFrente, setPreviewFrente] = useState(null);
  const [previewPerfil, setPreviewPerfil] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeCamera, setActiveCamera] = useState(null); // 'frente' or 'perfil'
  const [cameraStream, setCameraStream] = useState(null);
  
  const fileInputFrente = useRef(null);
  const fileInputPerfil = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'frente') {
          setFormData(prev => ({ ...prev, fotoFrente: reader.result }));
          setPreviewFrente(reader.result);
        } else {
          setFormData(prev => ({ ...prev, fotoPerfil: reader.result }));
          setPreviewPerfil(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } 
      });
      setCameraStream(stream);
      setActiveCamera(type);
      
      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('No se pudo acceder a la cámara. Por favor, permite el acceso o sube una foto desde archivo.');
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      
      if (activeCamera === 'frente') {
        setFormData(prev => ({ ...prev, fotoFrente: imageData }));
        setPreviewFrente(imageData);
      } else {
        setFormData(prev => ({ ...prev, fotoPerfil: imageData }));
        setPreviewPerfil(imageData);
      }
      
      stopCamera();
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setActiveCamera(null);
  };

  const removePhoto = (type) => {
    if (type === 'frente') {
      setFormData(prev => ({ ...prev, fotoFrente: null }));
      setPreviewFrente(null);
    } else {
      setFormData(prev => ({ ...prev, fotoPerfil: null }));
      setPreviewPerfil(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacto/rinomodelacion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          mensaje: '',
          fotoFrente: null,
          fotoPerfil: null
        });
        setPreviewFrente(null);
        setPreviewPerfil(null);
        if (onSuccess) onSuccess();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const PhotoUploadCard = ({ type, preview, label, instruction }) => (
    <Card className="p-4 border-2 border-dashed border-gray-200 hover:border-teal-400 transition-colors">
      <div className="text-center">
        <p className="font-semibold text-[#1C1C1A] mb-1">{label}</p>
        <p className="text-sm text-text-muted mb-4">{instruction}</p>
        
        {preview ? (
          <div className="relative inline-block">
            <img 
              src={preview} 
              alt={label}
              className="w-40 h-40 object-cover rounded-lg mx-auto"
            />
            <button
              type="button"
              onClick={() => removePhoto(type)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="w-32 h-32 bg-gray-100 rounded-lg mx-auto flex items-center justify-center">
              <User className="text-gray-400" size={48} />
            </div>
            
            <div className="flex gap-2 justify-center mt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => type === 'frente' ? fileInputFrente.current?.click() : fileInputPerfil.current?.click()}
                className="text-sm"
              >
                <Upload size={16} className="mr-1" />
                Archivo
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => startCamera(type)}
                className="text-sm"
              >
                <Camera size={16} className="mr-1" />
                Cámara
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Camera Modal */}
      {activeCamera && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#1C1C1A]">
                Foto de {activeCamera === 'frente' ? 'Frente' : 'Perfil'}
              </h3>
              <button
                onClick={stopCamera}
                className="text-text-muted hover:text-text-secondary"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="relative bg-black rounded-lg overflow-hidden mb-4">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full"
              />
              <div className="absolute bottom-4 left-0 right-0 text-center text-white text-sm bg-black/50 py-2">
                {activeCamera === 'frente' 
                  ? 'Mira directamente a la cámara' 
                  : 'Gira la cabeza mostrando tu perfil'}
              </div>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button
                onClick={capturePhoto}
                className="bg-gold hover:bg-teal-600 text-white px-8"
              >
                <Camera className="mr-2" size={20} />
                Capturar
              </Button>
              <Button
                variant="outline"
                onClick={stopCamera}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputFrente}
        accept="image/*"
        onChange={(e) => handleFileSelect(e, 'frente')}
        className="hidden"
      />
      <input
        type="file"
        ref={fileInputPerfil}
        accept="image/*"
        onChange={(e) => handleFileSelect(e, 'perfil')}
        className="hidden"
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl flex items-center gap-3">
            <Check className="text-green-500" size={24} />
            <div>
              <p className="font-semibold">¡Solicitud enviada correctamente!</p>
              <p className="text-sm">Nos pondremos en contacto contigo lo antes posible.</p>
            </div>
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
            <p className="font-semibold">Error al enviar el formulario</p>
            <p className="text-sm">Por favor, inténtalo de nuevo o contáctanos por teléfono.</p>
          </div>
        )}

        {/* Contact Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
              <User size={16} className="inline mr-2" />
              Nombre completo *
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              placeholder="Tu nombre"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
              <Phone size={16} className="inline mr-2" />
              Teléfono *
            </label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              placeholder="Tu teléfono"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
            <Mail size={16} className="inline mr-2" />
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            placeholder="tu@email.com (opcional)"
          />
        </div>

        {/* Photo Upload Section */}
        <div className="bg-teal-50 p-6 rounded-xl">
          <h3 className="text-lg font-semibold text-[#1C1C1A] mb-2 text-center">
            📸 Sube tus fotos (opcional)
          </h3>
          <p className="text-sm text-text-secondary text-center mb-6">
            Para una valoración previa más precisa, puedes adjuntar fotos de tu nariz
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhotoUploadCard 
              type="frente"
              preview={previewFrente}
              label="Foto de Frente"
              instruction="Mirando directamente a la cámara"
            />
            <PhotoUploadCard 
              type="perfil"
              preview={previewPerfil}
              label="Foto de Perfil"
              instruction="Mostrando el perfil de tu nariz"
            />
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
            <MessageSquare size={16} className="inline mr-2" />
            ¿Qué te gustaría mejorar?
          </label>
          <textarea
            name="mensaje"
            value={formData.mensaje}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
            placeholder="Cuéntanos qué aspecto de tu nariz te gustaría mejorar..."
          />
        </div>

        {/* Privacy Note */}
        <p className="text-xs text-text-muted text-center">
          Tus datos y fotos se tratarán con total confidencialidad según nuestra política de privacidad.
          Solo serán usados para evaluar tu caso y contactarte.
        </p>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || !formData.nombre || !formData.telefono}
          className="w-full bg-gold hover:bg-teal-600 text-white py-6 text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={20} />
              Enviando...
            </>
          ) : (
            'Solicitar Valoración'
          )}
        </Button>
      </form>
    </div>
  );
};

export default ContactFormRino;
